import React, { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * Why this exists: react-helmet-async 3 on React 19 no longer collects and
 * de-duplicates <Helmet> instances — each one renders real DOM elements that
 * React hoists into <head>. Two instances on one page means two <title>s and
 * doubled og:* tags, which is exactly what production showed after #3035.
 *
 * So the site-wide defaults may only render while no page-level SEO component
 * is mounted. Page components call useClaimHead(); the defaults watch the claim.
 */
const HeadClaimContext = createContext<{ claim: () => () => void; claimed: boolean } | null>(null);

export const HeadClaimProvider = ({ children }: { children: ReactNode }) => {
  const [claims, setClaims] = useState(0);
  const value = useMemo(() => ({
    claimed: claims > 0,
    claim: () => {
      setClaims((n) => n + 1);
      return () => setClaims((n) => Math.max(0, n - 1));
    },
  }), [claims]);
  return <HeadClaimContext.Provider value={value}>{children}</HeadClaimContext.Provider>;
};

/** Call from any component that renders its own <Helmet> with a <title>. */
export const useClaimHead = (): void => {
  const ctx = useContext(HeadClaimContext);
  const claim = ctx?.claim;
  useEffect(() => (claim ? claim() : undefined), [claim]);
};

export const DEFAULT_TITLE = 'SecurSpace | Nationwide Truck Parking & Storage';
export const DEFAULT_DESCRIPTION =
  'SecurSpace is an online marketplace that connects companies looking for parking and storage options to those with excess capacity.';
export const DEFAULT_IMAGE = 'https://static.secur.space/app-images/scurspace-logo.svg';

/** Site-wide <head> defaults, rendered only while no page has claimed the head. */
export const HeadDefaults = () => {
  const ctx = useContext(HeadClaimContext);
  if (ctx?.claimed) return null;
  return (
    <Helmet>
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:site_name" content="SecurSpace" />
      <meta property="og:type" content="website" />
      <meta property="og:image" content={DEFAULT_IMAGE} />
    </Helmet>
  );
};
